import React from "react";
import { withTranslation, type WithTranslation } from "react-i18next";

import Topbar from "./Common/Topbar";
import Footer from "./Common/Footer";
import MaintenancePage from "./MaintenancePage";
import Loader from "./Common/Loader";
import { getUserLanguage } from "config/i18n";
import { getCompanyBrandClass } from "helper/company";
import { withMaintenanceData, type MaintenanceQueryProps } from "api/queries/useMaintenance";
import type { UserCustomer, UserData } from "../types/state";
import { withAuthData, type AuthActions, type AuthInjectedProps } from "../context/AuthContext";

const TopbarAny = Topbar as unknown as React.ComponentType<Record<string, unknown>>;
const FooterAny = Footer as unknown as React.ComponentType<Record<string, unknown>>;
const MaintenancePageAny = MaintenancePage as unknown as React.ComponentType<
  Record<string, unknown>
>;

interface AppOwnProps extends WithTranslation {
  children: React.ReactElement;
  location?: { pathname: string };
}

interface AppDispatchProps {
  sessionActions: AuthActions;
}

type AppProps = AppOwnProps &
  AuthInjectedProps &
  AppDispatchProps &
  MaintenanceQueryProps & {
    userData: UserData;
    customerData: UserCustomer | Record<string, never>;
  };

function App({
  t,
  i18n,
  children,
  location,
  sessionActions,
  userLoggedIn,
  customerLoggedIn,
  userData,
  customerData,
  adminToken,
  maintenance,
}: AppProps) {
  const syncLanguage = React.useCallback(() => {
    const nextLanguage = getUserLanguage(userData);
    if (nextLanguage && i18n.resolvedLanguage !== nextLanguage) {
      i18n.changeLanguage(nextLanguage);
    }
  }, [i18n, userData]);

  React.useEffect(() => {
    syncLanguage();
  }, [syncLanguage]);

  const logout = (event: React.MouseEvent) => {
    event.preventDefault();
    sessionActions.logOutUser();
  };

  const logoutAdmin = (event: React.MouseEvent) => {
    event.preventDefault();
    sessionActions.logOutAdmin();
  };

  const unsetCustomer = (event: React.MouseEvent) => {
    event.preventDefault();
    sessionActions.unsetCustomer();
  };

  if (maintenance?.isMaintenance === null) {
    return <Loader />;
  } else if (maintenance?.isMaintenance) {
    return <MaintenancePageAny />;
  }

  const customer = customerData as UserCustomer & { company_name?: string };

  return (
    <div
      className={
        "App " + (customerLoggedIn ? "is" + getCompanyBrandClass(customer.company_name || "") : "")
      }
    >
      {userLoggedIn && customerLoggedIn ? (
        <TopbarAny
          customer={customer}
          user={userData}
          logout={logout}
          unsetCustomer={unsetCustomer}
          location={location}
          searchPlaceholder={t("topbar.search_placeholder")}
        />
      ) : null}
      <div className={"MainContainer " + (userLoggedIn && customerLoggedIn ? "" : "isFullsize")}>
        {React.cloneElement(children, {
          adminToken,
          userLoggedIn,
          userData,
          customerLoggedIn,
          customerData,
          maintenance,
          logout,
          logoutAdmin,
        })}
      </div>
      {userLoggedIn && customerLoggedIn ? <FooterAny /> : null}
    </div>
  );
}

export default withTranslation()(withAuthData(withMaintenanceData(App)));
